import type { FactMetadataOutDTO, FactParameterMetadataOutDTO } from '@api/ringil-api.v2';
import type { ComparisonNode, OperatorKey } from './ruleAst';

/**
 * Facts that take arguments — `expedition.shipmentDuration(first: max, last: min)`.
 *
 * Read generically from `FactMetadataOutDTO.factParameters`: no fact, argument or value is named
 * here, so the next parameterised fact works without a frontend release. An argument's label is
 * its `i18nKey`; each value's is `${i18nKey}_${value}`.
 */
export type FactParameter = FactParameterMetadataOutDTO;

/** A fact's arguments; `[]` (or absent, from older metadata) means it takes none. */
export const factParametersOf = (fact: Pick<FactMetadataOutDTO, 'factParameters'> | undefined): FactParameter[] => fact?.factParameters ?? [];

/** Every argument at its default. Written in full when a fact is picked, so the rule says which variant it means. */
export const defaultArgs = (parameters: FactParameter[]): Record<string, string> | undefined =>
  parameters.length > 0 ? Object.fromEntries(parameters.map(parameter => [parameter.name, parameter.defaultValue])) : undefined;

/** The value an argument has in a comparison: as written, else the default the backend applies. */
export const argValue = (node: Pick<ComparisonNode, 'args'>, parameter: FactParameter): string => node.args?.[parameter.name] ?? parameter.defaultValue;

/**
 * The comparison after its fact is switched. The operator is the new fact's and the value and the
 * arguments belonged to the old fact, so they go — but every other field is carried over, so a
 * field the backend adds later survives a builder edit without a frontend release.
 */
export const withFact = (node: ComparisonNode, fact: string, operator: OperatorKey, parameters: FactParameter[]): ComparisonNode => {
  const next: ComparisonNode = { ...node, fact, operator };
  delete next.value;
  delete next.args;
  const args = defaultArgs(parameters);
  return args ? { ...next, args } : next;
};

/**
 * Sets one argument. The others are filled in at their defaults, so a rule written as plain
 * `expedition.shipmentDuration > 2 days` becomes fully explicit the first time it is edited.
 */
export const withArg = (node: ComparisonNode, parameters: FactParameter[], name: string, value: string): ComparisonNode => ({
  ...node,
  args: { ...defaultArgs(parameters), ...node.args, [name]: value },
});

/** `(first: max, last: min)` — the arguments as the rule text writes them, or '' for none. */
export const argsText = (args: Record<string, string> | null | undefined): string => {
  const entries = Object.entries(args ?? {});
  return entries.length > 0 ? `(${entries.map(([name, value]) => `${name}: ${value}`).join(', ')})` : '';
};

type Translate = (key: string, defaultText?: string) => string;

/** An argument's label — *First loading* — falling back to its DSL name. */
export const argLabel = (tr: Translate, parameter: FactParameter): string => tr(parameter.i18nKey, parameter.name);

/** One value's label — *latest end* — falling back to its DSL spelling, which is what the rule stores. */
export const argValueLabel = (tr: Translate, parameter: FactParameter, value: string): string => tr(`${parameter.i18nKey}_${value}`, value);

/**
 * `First loading: latest end · Last unloading: earliest start`, for the explain trace. An argument
 * the metadata does not describe (an old log row, a since-removed parameter) is shown as written.
 */
export const describeArgs = (tr: Translate, parameters: FactParameter[], args: Record<string, string> | null | undefined): string =>
  Object.entries(args ?? {})
    .map(([name, value]) => {
      const parameter = parameters.find(candidate => candidate.name === name);
      return parameter ? `${argLabel(tr, parameter)}: ${argValueLabel(tr, parameter, value)}` : `${name}: ${value}`;
    })
    .join(' · ');
